import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import type { WorkHoursIdentity } from "@torre/work-hours";
import { useTheme } from "../../../context/ThemeContext";
import { fetchTeamMembers } from "../../team/services/teamService";
import {
  fetchIdentities,
  fetchMe,
  fetchUnmatched,
  prelinkIdentity,
  saveIdentityForUser,
  setIdentityTracking,
  type IdentityRow,
  type UnmatchedAuthor,
  type WorkHoursMe,
} from "../services/workHoursService";
import { repoUrl } from "../lib/github";
import { describeRequestFailure } from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Identidad GitHub (#537), parte de ADMINISTRACIÓN: el admin liga cada identidad
// con su colaborador del módulo Equipo (asistencia #536) y resuelve los autores
// de commits que aún no tienen usuario.
//
// «Mi identidad» (ligar mi usuario de GitHub y mis emails de git) y «Carga tus
// horas con Claude» ya no están aquí: son conexiones personales y viven en
// Perfil → Integraciones (pages/profile/GithubIdentityIntegration.tsx y
// ClaudeHoursGuide.tsx). El regreso del OAuth de GitHub sigue llegando a esta
// ruta —la fija el backend—, así que se reenvía al perfil con sus parámetros.
// ---------------------------------------------------------------------------

/** Dónde vive ahora la identidad personal. */
export const PROFILE_INTEGRATIONS_PATH = "/profile?tab=integrations";

export default function IdentitySection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;

  const [me, setMe] = useState<WorkHoursMe | null>(null);
  const [identities, setIdentities] = useState<IdentityRow[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedAuthor[]>([]);
  const [members, setMembers] = useState<{ id: string; name: string }[]>([]);
  // Clave i18n, no texto: `load` no depende de `t`. Un fallo se dice; nunca se
  // pinta el estado vacío («nadie ha registrado su identidad») sobre un error.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [prelinkDraft, setPrelinkDraft] = useState<Record<string, string>>({});
  // Regreso de GitHub (`?github=…`): lo atiende el perfil, no esta pantalla.
  const githubReturn = searchParams.get("github") !== null;

  const load = useCallback(async () => {
    setErrorKey(null);
    let m: WorkHoursMe;
    try {
      m = await fetchMe();
    } catch {
      setErrorKey("workHours.identity.loadError");
      return;
    }
    setMe(m);
    if (!m.isAdmin) return;
    try {
      // Los repos de cada persona vienen resueltos en el propio listado (una
      // sola lectura acotada en el servidor): ya no hay una llamada por persona.
      const [ids, un] = await Promise.all([fetchIdentities(), fetchUnmatched()]);
      setIdentities(ids);
      setUnmatched(un);
    } catch {
      setIdentities([]);
      setUnmatched([]);
      setErrorKey("workHours.identity.loadError");
      return;
    }
    // El módulo Equipo es complementario: si falla, las identidades se ven igual.
    const team = await fetchTeamMembers().catch(() => ({ items: [] }));
    setMembers(team.items.map((x) => ({ id: x.id, name: [x.name, x.lastName].filter(Boolean).join(" ") })));
  }, []);

  useEffect(() => {
    if (!githubReturn) void load();
  }, [load, githubReturn]);

  const savePrelink = async (identity: IdentityRow) => {
    const email = (prelinkDraft[identity.userId] ?? "").trim();
    try {
      const res = await prelinkIdentity({ userId: identity.userId, email: email || null });
      await Swal.fire({
        icon: "success",
        title: t(res.status === "claimed" ? "workHours.identity.prelinkClaimed" : "workHours.identity.prelinkDone"),
        timer: 2500,
      });
      setPrelinkDraft((d) => ({ ...d, [identity.userId]: "" }));
      await load();
    } catch (err) {
      const failure = describeRequestFailure(err);
      await Swal.fire({ icon: "error", title: t("workHours.identity.prelinkError"), text: failure.serverMessage ?? undefined });
    }
  };

  const toggleTracking = async (identity: IdentityRow) => {
    const excluding = identity.trackHours !== false;
    if (excluding) {
      const answer = await Swal.fire({
        icon: "warning",
        title: t("workHours.identity.markContractor"),
        text: t("workHours.identity.contractorConfirm", { name: identity.userName ?? identity.githubLogin ?? identity.userId }),
        showCancelButton: true,
      });
      if (!answer || !answer.isConfirmed) return;
    }
    try {
      await setIdentityTracking({ userId: identity.userId, trackHours: !excluding, ...(excluding ? { reason: "contratista" } : {}) });
      await load();
    } catch (err) {
      const failure = describeRequestFailure(err);
      await Swal.fire({ icon: "error", title: t("workHours.identity.trackingError"), text: failure.serverMessage ?? undefined });
    }
  };

  const updateIdentity = async (identity: WorkHoursIdentity, patch: Partial<WorkHoursIdentity>) => {
    await saveIdentityForUser(identity.userId, {
      githubLogin: patch.githubLogin ?? identity.githubLogin ?? undefined,
      gitEmails: patch.gitEmails ?? identity.gitEmails ?? [],
      teamMemberId: patch.teamMemberId === undefined ? identity.teamMemberId ?? null : patch.teamMemberId,
    });
    await load();
  };

  const linkAuthor = async (author: UnmatchedAuthor, userId: string) => {
    const identity = identities.find((i) => i.userId === userId);
    if (!identity) return;
    const gitEmails = [...new Set([...(identity.gitEmails ?? []), ...(author.authorEmail ? [author.authorEmail] : [])])];
    await updateIdentity(identity, { gitEmails, githubLogin: identity.githubLogin || author.authorLogin || undefined });
  };

  // La ventana es la misma para todo el equipo: basta la primera que la declare.
  const reposWindow = identities.find((i) => i.reposWindow)?.reposWindow ?? null;

  if (githubReturn) {
    // Se conservan `github`, `login`, `code` y `state`: el `code` es de un solo
    // uso y lo canjea la tarjeta de GitHub del perfil, con la sesión.
    const forwarded = new URLSearchParams(searchParams);
    forwarded.set("tab", "integrations");
    return <Navigate to={`/profile?${forwarded.toString()}`} replace />;
  }

  return (
    <div className="w-full max-w-6xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div>
        <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.identity")}</h1>
        <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.identity.adminSubtitle")}</p>
      </div>

      {/* La parte personal se mudó: se dice y se lleva ahí, no se deja a nadie buscando. */}
      <div className={`${card} flex flex-wrap items-center gap-3`} data-testid="identity-moved">
        <i className={`ri-github-fill text-2xl ${tc.text}`} />
        <p className={`min-w-0 flex-1 text-sm ${tc.textSecondary}`}>{t("workHours.identity.movedNote")}</p>
        <Link to={PROFILE_INTEGRATIONS_PATH} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white">
          {t("workHours.identity.movedOpen")}
        </Link>
      </div>

      {errorKey && (
        <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500" data-testid="identity-error">
          <p>{t(errorKey)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-red-500/40 px-2 py-1 text-xs font-medium">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}


      {me?.isAdmin && !errorKey && (
        <>
          <div className={card}>
            <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.identity.teamTitle")}</p>
            {/* La columna de repos NO es todo el histórico: sale de la ventana
                que declara el propio backend (`reposWindow`). Se dice. */}
            {reposWindow && (
              <p className={`mb-2 text-[11px] ${tc.textSecondary}`} data-testid="identity-repos-window">
                {t("workHours.identity.reposWindow", { days: reposWindow.days, from: reposWindow.from, to: reposWindow.to })}
              </p>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-xs ${tc.textSecondary}`}>
                  <th className="py-1">{t("workHours.people.person")}</th>
                  <th className="py-1">GitHub</th>
                  <th className="py-1">{t("workHours.identity.gitEmails")}</th>
                  <th className="py-1">{t("workHours.identity.repos")}</th>
                  <th className="py-1">{t("workHours.identity.teamMember")}</th>
                  <th className="py-1">{t("workHours.identity.status")}</th>
                </tr>
              </thead>
              <tbody>
                {identities.map((i) => (
                  <tr key={i.id} className={`border-t ${tc.border} ${tc.text}`}>
                    <td className="py-1.5">{i.userName ?? i.email ?? i.userId}</td>
                    <td className="py-1.5 font-mono text-xs">{i.githubLogin ?? "—"}</td>
                    <td className="py-1.5 text-xs">{(i.gitEmails ?? []).join(", ") || "—"}</td>
                    {/* Repos ligados a la identidad: enlazan a GitHub para ver
                        de un vistazo a qué está atada cada persona. `repos` en
                        `null` es «no se pudo calcular», NO «sin repos». */}
                    <td className="py-1.5 text-xs" data-testid={`identity-repos-${i.userId}`}>
                      {(() => {
                        if (i.repos == null) {
                          return (
                            <span className="text-red-500">
                              <span title={i.reposError ?? undefined}>{t("workHours.identity.reposError")}</span>{" "}
                              <button type="button" onClick={() => void load()} className="underline">
                                {t("workHours.retry")}
                              </button>
                            </span>
                          );
                        }
                        if (i.repos.length === 0) {
                          return (
                            <span className={tc.textSecondary}>
                              {t("workHours.identity.noRepos", { days: i.reposWindow?.days ?? 0 })}
                            </span>
                          );
                        }
                        return i.repos.map((r, idx) => {
                          const url = repoUrl(r);
                          return (
                            <span key={r}>
                              {idx > 0 && ", "}
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t("workHours.repositories.openOnGithub", { repo: r })}
                                  className="text-violet-500 hover:underline"
                                >
                                  {r}
                                </a>
                              ) : (
                                r
                              )}
                            </span>
                          );
                        });
                      })()}
                    </td>
                    <td className="py-1.5">
                      <select
                        aria-label={`${t("workHours.identity.teamMember")} ${i.userName ?? i.userId}`}
                        value={i.teamMemberId ?? ""}
                        onChange={(e) => void updateIdentity(i, { teamMemberId: e.target.value || null })}
                        className={`${inputClass(isDarkMode)} py-1 text-xs`}
                      >
                        <option value="">{t("workHours.identity.noTeamMember")}</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    {/* Estado (#841): cuenta sintética y su pre-vínculo por
                        correo, y contratista externo (horas no registradas). */}
                    <td className="py-1.5 text-xs" data-testid={`identity-status-${i.userId}`}>
                      <div className="flex flex-col gap-1">
                        {i.trackHours === false && (
                          <span className="w-fit rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-600">{t("workHours.identity.contractor")}</span>
                        )}
                        {i.syntheticUser && <span className={tc.textSecondary}>{t("workHours.identity.synthetic")}</span>}
                        {i.pendingEmail && <span className={tc.textSecondary}>{t("workHours.identity.pendingEmail", { email: i.pendingEmail })}</span>}
                        {i.syntheticUser && (
                          <div className="flex items-center gap-1" title={t("workHours.identity.prelinkHint")}>
                            <input
                              type="email"
                              aria-label={`${t("workHours.identity.prelink")} ${i.userName ?? i.userId}`}
                              placeholder={t("workHours.identity.prelinkPlaceholder")}
                              value={prelinkDraft[i.userId] ?? ""}
                              onChange={(e) => setPrelinkDraft((d) => ({ ...d, [i.userId]: e.target.value }))}
                              className={`${inputClass(isDarkMode)} w-44 py-1 text-xs`}
                            />
                            <button
                              type="button"
                              disabled={!(prelinkDraft[i.userId] ?? "").trim()}
                              onClick={() => void savePrelink(i)}
                              className="rounded-md bg-violet-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                            >
                              {t("workHours.identity.prelink")}
                            </button>
                          </div>
                        )}
                        <button type="button" onClick={() => void toggleTracking(i)} className="w-fit text-[11px] text-violet-500 hover:underline">
                          {i.trackHours === false ? t("workHours.identity.trackAgain") : t("workHours.identity.markContractor")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {identities.length === 0 && (
                  <tr>
                    <td colSpan={6} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                      {t("workHours.identity.noneYet")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={card}>
            <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.identity.unmatchedTitle")}</p>
            <p className={`mb-2 text-xs ${tc.textSecondary}`}>{t("workHours.identity.unmatchedHint")}</p>
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-xs ${tc.textSecondary}`}>
                  <th className="py-1">{t("workHours.identity.author")}</th>
                  <th className="py-1 text-right">{t("workHours.kpi.commits")}</th>
                  <th className="py-1">Repos</th>
                  <th className="py-1">{t("workHours.identity.linkTo")}</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map((a) => (
                  <tr key={`${a.authorLogin}-${a.authorEmail}`} className={`border-t ${tc.border} ${tc.text}`}>
                    <td className="py-1.5 text-xs">
                      <span className="font-mono">{a.authorLogin ?? "—"}</span> · {a.authorEmail ?? a.authorName}
                    </td>
                    <td className="py-1.5 text-right">{a.commits}</td>
                    <td className="py-1.5 text-xs">{a.repos.join(", ")}</td>
                    <td className="py-1.5">
                      <select
                        defaultValue=""
                        aria-label={`${t("workHours.identity.linkTo")} ${a.authorLogin ?? a.authorEmail}`}
                        onChange={(e) => e.target.value && void linkAuthor(a, e.target.value)}
                        className={`${inputClass(isDarkMode)} py-1 text-xs`}
                      >
                        <option value="">—</option>
                        {identities.map((i) => (
                          <option key={i.userId} value={i.userId}>
                            {i.userName ?? i.userId}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                {unmatched.length === 0 && (
                  <tr>
                    <td colSpan={4} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                      {t("workHours.identity.allMatched")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
